/* eslint-disable */
import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import jwtInterceoptor from "../api/jwtInterceptor";
import { GET_API_URLS } from "../utils/constant";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    stores: null,
    sub_admin: null,
    promotions: null,
    webPromotions: null,
    webPromotionsCategory: null,
    blogs: null,
    cities: null,
    province: null,
    banner: null,
  });

  const [modules, setModules] = useState(null);

  const [userData, setUserData] = useState({
    firstName: "User",
    lastName: "",
    modules: [],
  });

  const [allowedModules, setAllowedModules] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // console.log("fetched data -->", auth);

  const [user, setUser] = useState(() => {
    if (localStorage.getItem("tokens")) {
      let tokens = JSON.parse(localStorage.getItem("tokens"));
      return jwtDecode(tokens.access);
    }
    return null;
  });

  const navigate = useNavigate();

  // Reset all state when user changes
  const resetState = () => {
    console.log("Resetting all application state");
    setAuth({
      stores: null,
      sub_admin: null,
      promotions: null,
      webPromotions: null,
      webPromotionsCategory: null,
      blogs: null,
      cities: null,
      province: null,
      banner: null,
    });
    setModules(null);
    setUserData({
      firstName: "User",
      lastName: "",
      modules: [],
    });
    setAllowedModules(null);
    setSelectedProject(null);
    console.log("State reset complete");
  };

  // Fetching user data
  useEffect(() => {
    if (user) {
      console.log("Fetching user data for user ID:", user.user_id);
      jwtInterceoptor
        .get(GET_API_URLS.SUB_ADMIN_URL + `${user.user_id}/`)
        .then((response) => {
          console.log("User data fetched successfully:", response.data);
          setUserData({
            firstName: response.data.first_name,
            lastName: response.data.last_name,
            modules: response.data.projects,
          });
          getModules();
        })
        .catch(() => {
          console.log("Failed to fetch user data, resetting to defaults");
          setUserData({
            firstName: "",
            lastName: "",
            modules: [],
          });
        });
    } else {
      // Reset state when user is null (logout)
      console.log("User is null, resetting application state");
      resetState();
    }
  }, [user]);

  const getModules = async () => {
    try {
      const response = await jwtInterceoptor.get(GET_API_URLS.MODULE);
      console.log("modules", response.data);
      setModules(response.data);
    } catch (error) {
      console.log("Error", error);
    }
  };

  const login = async (payload) => {
    try {
      console.log("Login attempt for user:", payload.email);
      const apiResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/admin/accounts/login/`,
        payload
      );
      console.log("Login successful, setting tokens and user data");
      localStorage.setItem("tokens", JSON.stringify(apiResponse.data));
      setUser(jwtDecode(apiResponse.data.access));
      navigate("/dashboard");
      return { msg: false };
    } catch (error) {
      let errorMsg;
      if (!error?.response) {
        errorMsg = "No Server Response";
      } else if (error.response?.status === 400) {
        errorMsg = "Missing username or password";
      } else if (error.response?.status === 401) {
        errorMsg = "Unauthorized Access";
      } else {
        errorMsg = "Login Failed!";
      }
      console.log("Login failed:", errorMsg);
      return { msg: errorMsg };
    }
  };

  const logout = () => {
    console.log("Logout called - clearing user state");
    // Clear tokens from localStorage
    localStorage.removeItem("tokens");
    
    // Reset user state (this will trigger useEffect to reset all other state)
    setUser(null);
    
    // Navigate to login page
    navigate("/");
  };

  useEffect(() => {
    if (modules && userData.modules) {
      console.log("Processing modules for user:", userData.firstName, userData.lastName);
      comparingModuleHandler(modules, userData.modules);
    }
  }, [modules, userData.modules]);

  // Additional effect to reset selectedProject when user changes
  useEffect(() => {
    // Reset selectedProject when user changes to prevent cross-user project selection
    if (userData.modules && userData.modules.length === 0) {
      console.log("User has no modules, resetting selectedProject");
      setSelectedProject(null);
    } else if (userData.modules && userData.modules.length > 0) {
      // Even if user has modules, we should reset selectedProject to prevent
      // using the previous user's selection
      console.log("User has modules, but resetting selectedProject to ensure clean state");
      setSelectedProject(null);
    }
  }, [userData.modules]);

  // Cleanup effect when component unmounts or user changes
  useEffect(() => {
    return () => {
      // Cleanup function to reset state when component unmounts
      if (!user) {
        console.log("User logged out, cleaning up state");
        resetState();
      }
    };
  }, [user]);

  const comparingModuleHandler = (allModulesData, userProjectData) => {
    console.log("Comparing modules - All modules:", allModulesData, "User projects:", userProjectData);
    const allowedProjects = [];
    
    // Get user's accessible project IDs
    const userProjectIds = userProjectData.map(project => project.id);
    
    // Iterate through user's accessible projects
    userProjectData.forEach(userProject => {
      // Check if user has access to this project
      if (!userProjectIds.includes(userProject.id)) {
        return; // Skip if no project access
      }
      
      // Find the corresponding project in all modules data
      const projectInAllModules = allModulesData.find(
        project => project.id === userProject.id
      );
      
      if (projectInAllModules && userProject.modules) {
        // Get user's accessible module IDs for this project
        const userModuleIds = userProject.modules.map(module => module.id);
        
        // Filter project modules based on user permissions
        const accessibleModules = projectInAllModules.modules.filter(
          module => userModuleIds.includes(module.id) && module.id !== 8
        );
        
        // Only add project if it has accessible modules
        if (accessibleModules.length > 0) {
          allowedProjects.push({
            id: userProject.id,
            name: userProject.name,
            modules: accessibleModules
          });
        }
      }
    });
    
    console.log("Allowed projects for user:", allowedProjects);
    setAllowedModules(allowedProjects);
    
    // Always reset selected project when user data changes (new user login)
    // This ensures the previous user's selection doesn't persist
    if (allowedProjects.length > 0) {
      console.log("Setting selectedProject to first allowed project:", allowedProjects[0].name);
      setSelectedProject(allowedProjects[0]);
    } else {
      console.log("No allowed projects, resetting selectedProject to null");
      setSelectedProject(null);
    }
  };

  console.log("allowedModules", allowedModules);

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        login, 
        logout,
        auth, 
        setAuth, 
        userData, 
        modules, 
        allowedModules,
        selectedProject, 
        setSelectedProject 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
